import { useEffect, useState } from "react";
import { logger } from "@/lib/logger";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Truck } from "lucide-react";
import { stageViews } from "@/lib/stages";

const fmt = (n) => Math.round(Number(n || 0)).toLocaleString("en-IN"); // production units are whole pieces

function Stat({ label, value, cls, hint }) {
  return (
    <div className="text-center">
      <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`font-mono-data text-lg font-semibold ${cls || "text-slate-700"}`}>{value}</p>
      {hint && <p className="text-[10px] text-slate-400">{hint}</p>}
    </div>
  );
}

// ctx = { order, idx } for an OSP stage. Supports partial shipments (one gate pass each).
export default function ShipToVendorDialog({ ctx, vendors = [], onClose, onSubmit }) {
  const [vendorId, setVendorId] = useState("");
  const [qty, setQty] = useState("");
  const [process, setProcess] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const active = vendors.filter((v) => v.active !== false);

  const resetKey = ctx ? `${ctx.order.id}:${ctx.idx}` : "";
  useEffect(() => {
    if (resetKey) {
      const first = active[0];
      setVendorId(first?.id || "");
      setProcess(first?.process || "");
      setQty("");
      setNotes("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  if (!ctx) return null;
  const { order, idx } = ctx;
  const views = stageViews(order);
  const v = views[idx];
  if (!v) return null;
  const target = Number(order.target_quantity) || 0;
  const prevName = idx > 0 ? views[idx - 1].name : null;
  const vendor = active.find((x) => x.id === vendorId);
  const num = Number(qty);
  const valid = !!vendorId && qty !== "" && num > 0 && num <= v.available + 1e-6;

  const pickVendor = (id) => {
    setVendorId(id);
    const vv = active.find((x) => x.id === id);
    if (vv?.process) setProcess(vv.process);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    try { await onSubmit(order, idx, { vendor_id: vendorId, quantity: num, process, notes }); onClose(); }
    catch (err) { logger.error("Ship-to-vendor failed (toast shown by caller):", err); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="ship-to-vendor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Truck className="h-4 w-4 text-orange-600" /> Ship to Vendor · {order.order_code}</DialogTitle>
          <DialogDescription>Stage {idx + 1} · <b>{v.name}</b> (Outside Processing) — {order.product_name}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-lg border border-orange-200 bg-orange-50/60 p-3 grid grid-cols-3 gap-2">
            <Stat label="Available to Send" value={fmt(v.available)} cls="text-orange-700" hint={prevName ? `from ${prevName}` : "order quantity"} />
            <Stat label="Already Sent" value={fmt(v.shipped)} hint={`${v.gatePassCount} gate pass${v.gatePassCount === 1 ? "" : "es"}`} />
            <Stat label="Order Qty" value={fmt(target)} hint="pcs" />
          </div>

          <div className="space-y-1.5">
            <Label>Quantity to Send</Label>
            <div className="flex gap-2">
              <Input type="number" min="0.001" step="any" max={v.available} value={qty} onChange={(e) => setQty(e.target.value)} placeholder={`max ${fmt(v.available)}`} required autoFocus className="font-mono-data" data-testid="ship-qty" />
              <Button type="button" variant="outline" onClick={() => setQty(String(v.available))} className="shrink-0 text-xs" data-testid="ship-send-all">Send All ({fmt(v.available)})</Button>
            </div>
            <p className={`text-[11px] ${qty !== "" && num > v.available ? "text-red-600" : "text-slate-400"}`}>Partial shipments allowed · max {fmt(v.available)}</p>
          </div>

          <div className="space-y-1.5">
            <Label>OSP Vendor</Label>
            <SearchableSelect
              value={vendorId}
              onValueChange={pickVendor}
              placeholder="— Select Vendor —"
              searchPlaceholder="Search vendors…"
              emptyText="No OSP vendors found."
              options={active.map((x) => ({ value: x.id, label: `${x.name} · ${x.process || "process"} (${x.turnaround_days || 0}d)`, keywords: x.process }))}
              testid="ship-vendor-select"
            />
            {active.length === 0 && <p className="text-[11px] text-amber-600">No OSP vendors yet — add one on the OSP tab first.</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Expected Back</Label>
              <Input value={vendor ? new Date(Date.now() + (vendor.turnaround_days || 0) * 86400000).toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" }) : "—"} disabled data-testid="ship-expected-back" />
            </div>
            <div className="space-y-1.5">
              <Label>Process</Label>
              <Input value={process} onChange={(e) => setProcess(e.target.value)} placeholder="e.g. Buffing" data-testid="ship-process" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Notes (Optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Challan / vehicle no…" data-testid="ship-notes" />
          </div>

          <DialogFooter className="justify-between">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={!valid || saving} className="bg-orange-600 hover:bg-orange-700" data-testid="ship-submit">
              {saving ? "Shipping…" : valid ? `Create Gate Pass · Send ${fmt(num)}` : "Create Gate Pass"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
